import { useEffect, useRef, useState } from 'react'
import { Loader2, ShieldCheck, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { authApi } from '../api/services.js'

const RESEND_COOLDOWN = 60

export default function ActionOtpModal({
  open,
  scope,
  actionLabel = 'sensitive action',
  onVerified,
  onClose,
}) {
  const [otp, setOtp] = useState('')
  const [maskedEmail, setMaskedEmail] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [sending, setSending] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const timerRef = useRef(null)

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const startCountdown = () => {
    clearTimer()
    setCountdown(RESEND_COOLDOWN)
    timerRef.current = window.setInterval(() => {
      setCountdown((current) => {
        if (current <= 1) {
          clearTimer()
          return 0
        }
        return current - 1
      })
    }, 1000)
  }

  const sendOtp = async () => {
    try {
      setSending(true)
      const res = await authApi.sendActionOtp({ actionLabel })
      setMaskedEmail(res?.email || '')
      startCountdown()
      toast.success('Verification code sent')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send verification code')
    } finally {
      setSending(false)
    }
  }

  useEffect(() => {
    if (!open) return undefined
    setOtp('')
    setMaskedEmail('')
    sendOtp()
    return clearTimer
  }, [open])

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otp.trim())) {
      toast.error('Enter the 6-digit code')
      return
    }

    try {
      setVerifying(true)
      const res = await authApi.verifyActionOtp({ otp: otp.trim(), scope })
      setOtp('')
      onVerified(res?.actionToken)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid or expired code')
    } finally {
      setVerifying(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 p-4">
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <button onClick={onClose} className="rounded-xl p-1 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <h3 className="mt-5 text-xl font-bold text-gray-900">Verify Your Identity</h3>
        <p className="mt-2 text-sm leading-6 text-gray-500">
          A 6-digit code has been sent to{' '}
          <span className="font-semibold text-gray-900">{maskedEmail || 'your registered email'}</span>.
        </p>
        <span className="mt-3 inline-flex rounded-xl bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
          Action: {actionLabel}
        </span>

        <input
          value={otp}
          onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
          className="mt-5 w-full rounded-2xl border-2 border-primary-500 bg-gray-50 px-4 py-4 text-center text-2xl font-bold tracking-[0.35em] text-gray-900 outline-none focus:ring-4 focus:ring-primary-100"
          placeholder="------"
          inputMode="numeric"
          autoFocus
        />

        <button
          onClick={verifyOtp}
          disabled={verifying || otp.length < 6}
          className="btn-primary mt-5 w-full disabled:opacity-50"
        >
          {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Verify & Continue
        </button>

        <div className="mt-4 flex justify-center">
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
          ) : countdown > 0 ? (
            <p className="text-sm text-gray-400">Resend in {countdown}s</p>
          ) : (
            <button onClick={sendOtp} className="text-sm font-semibold text-primary-600 hover:text-primary-700">
              Resend Code
            </button>
          )}
        </div>

        <p className="mt-4 text-center text-xs leading-5 text-gray-400">
          Code expires in 10 minutes. Do not share it with anyone.
        </p>
      </div>
    </div>
  )
}
